import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  formatManches,
  formatSetScore,
  isWinner,
  qualificationLabel,
  resolvePlayer,
  statusLabel,
} from "./displayHelpers";
import PoolMatchRow from "./PoolMatchRow";
import {
  accentColor,
  chipQuietSx,
  ctaOutlinedSx,
  ctaTextSx,
  isDarkMode,
  statusChipSx,
  surfaceSx,
} from "./resultatsTheme";
import { buildPlayerJourney } from "./searchHelpers";
import type { Game, PlayerDirectory, ResultsDoc } from "./types";

interface PlayerJourneyViewProps {
  doc: ResultsDoc;
  licence: string | null;
  players: PlayerDirectory;
  onBack: () => void;
  onOpenPool: (poolId: string) => void;
  onOpenRound: (roundId: string) => void;
  onSelectLicence?: (licence: string) => void;
}

function JourneyMatchDetail({
  game,
  licence,
  players,
}: {
  game: Game;
  licence: string;
  players: PlayerDirectory;
}): React.ReactElement {
  const theme = useTheme();
  const manches = formatManches(game);
  const opponent = game.players.find((p) => p && p !== licence) ?? null;
  const oppIdentity = resolvePlayer(opponent, players);
  const won = isWinner(licence, game);

  return (
    <Box
      sx={{
        mt: 1.25,
        ml: { xs: 0, sm: 4.5 },
        p: 1.25,
        borderRadius: 2,
        bgcolor: (t) =>
          alpha(
            t.palette.mode === "dark"
              ? t.palette.common.white
              : t.palette.primary.main,
            t.palette.mode === "dark" ? 0.05 : 0.04
          ),
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {game.status === "EXEMPT"
          ? "Exempt — pas de rencontre jouée"
          : `Adversaire : ${oppIdentity.displayName}`}
      </Typography>
      {oppIdentity.club && game.status !== "EXEMPT" && (
        <Typography variant="caption" color="text.secondary">
          {oppIdentity.club}
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
        <Typography
          sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
        >
          {formatSetScore(game)}
        </Typography>
        <Chip
          size="small"
          label={statusLabel(game.status)}
          variant="outlined"
          sx={statusChipSx(theme)}
        />
        {won && game.status !== "EXEMPT" && (
          <Chip size="small" color="success" label="Vainqueur" />
        )}
      </Stack>
      {manches && (
        <Typography variant="caption" color="text.secondary" display="block">
          Manches : {manches}
        </Typography>
      )}
    </Box>
  );
}

export default function PlayerJourneyView({
  doc,
  licence,
  players,
  onBack,
  onOpenPool,
  onOpenRound,
}: PlayerJourneyViewProps): React.ReactElement {
  const theme = useTheme();
  const isDark = isDarkMode(theme);
  const accent = accentColor(theme);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  const journey = useMemo(
    () => (licence ? buildPlayerJourney(doc, licence, players) : null),
    [doc, licence, players]
  );

  if (!licence) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Recherchez un joueur ci-dessus pour afficher son parcours.
      </Alert>
    );
  }

  if (!journey) {
    return (
      <Stack spacing={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          color="inherit"
          sx={{ alignSelf: "flex-start", ...ctaTextSx(theme) }}
        >
          Retour
        </Button>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Aucun parcours trouvé pour la licence {licence} dans ce tableau.
        </Alert>
      </Stack>
    );
  }

  const identity = resolvePlayer(licence, players);
  const qual = qualificationLabel(journey.qualification);

  return (
    <Stack spacing={2.75}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          color="inherit"
          sx={ctaTextSx(theme)}
        >
          Retour
        </Button>
        {journey.poolId && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => onOpenPool(journey.poolId!)}
            sx={ctaOutlinedSx(theme)}
          >
            Voir la poule
          </Button>
        )}
      </Box>

      <Box sx={{ ...surfaceSx(theme), p: { xs: 2, sm: 2.5 } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            mb: 0.4,
            letterSpacing: "-0.02em",
            fontSize: { xs: "1.35rem", sm: "1.5rem" },
          }}
        >
          {identity.displayName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {[
            identity.club,
            identity.points != null ? `${identity.points} pts` : null,
          ]
            .filter(Boolean)
            .join(" · ") || `Licence ${licence}`}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1.5 }}
        >
          <Chip
            size="small"
            label={doc.label}
            variant="outlined"
            sx={chipQuietSx(theme)}
          />
          {journey.poolLabel && (
            <Chip
              size="small"
              label={
                journey.rank != null
                  ? `${journey.poolLabel} · #${journey.rank}`
                  : journey.poolLabel
              }
              variant="outlined"
              sx={chipQuietSx(theme)}
            />
          )}
          {qual && (
            <Chip
              size="small"
              color={
                journey.qualification === "QUALIFIED"
                  ? "success"
                  : journey.qualification === "ELIMINATED"
                    ? "default"
                    : "warning"
              }
              label={qual}
              sx={{ fontWeight: 700 }}
            />
          )}
          {journey.furthestRoundLabel && (
            <Chip
              size="small"
              color="secondary"
              label={`Tour atteint : ${journey.furthestRoundLabel}`}
              sx={
                isDark
                  ? { color: "grey.900", fontWeight: 700 }
                  : { fontWeight: 700 }
              }
            />
          )}
        </Stack>
      </Box>

      <Box>
        <Typography
          variant="overline"
          sx={{
            display: "block",
            fontWeight: 800,
            letterSpacing: "0.1em",
            color: accent,
            mb: 1.5,
          }}
        >
          Parcours
        </Typography>

        <Box sx={{ position: "relative", pl: 0.25 }}>
          {journey.steps.map((step, index) => {
            const open = expandedStep === step.id;
            const clickable = Boolean(step.game) || step.kind === "pool";
            const last = index === journey.steps.length - 1;
            return (
              <Box
                key={step.id}
                sx={{
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: "28px minmax(0, 1fr)",
                  gap: 1.25,
                  pb: last ? 0 : 1.5,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 800,
                      fontSize: "0.75rem",
                      zIndex: 1,
                      bgcolor: step.confirmed
                        ? alpha(
                            isDark
                              ? theme.palette.secondary.main
                              : theme.palette.primary.main,
                            isDark ? 0.28 : 0.16
                          )
                        : alpha(theme.palette.text.disabled, 0.2),
                      color: "text.primary",
                      border: `1.5px solid ${
                        step.confirmed
                          ? accent
                          : alpha(theme.palette.text.disabled, 0.4)
                      }`,
                    }}
                  >
                    {index + 1}
                  </Box>
                  {!last && (
                    <Box
                      sx={{
                        width: 2,
                        flex: 1,
                        mt: 0.5,
                        borderRadius: 1,
                        bgcolor: alpha(
                          isDark
                            ? theme.palette.common.white
                            : theme.palette.primary.main,
                          isDark ? 0.16 : 0.14
                        ),
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    ...surfaceSx(theme),
                    borderRadius: 2.5,
                    px: 1.5,
                    py: 1.2,
                    bgcolor: step.confirmed
                      ? undefined
                      : alpha(theme.palette.action.disabled, 0.04),
                  }}
                >
                  <Box
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={() => {
                      if (step.kind === "pool" && journey.poolId) {
                        onOpenPool(journey.poolId);
                        return;
                      }
                      if (step.game) {
                        setExpandedStep((cur) =>
                          cur === step.id ? null : step.id
                        );
                      }
                    }}
                    onKeyDown={(event) => {
                      if (!clickable) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (step.kind === "pool" && journey.poolId) {
                          onOpenPool(journey.poolId);
                        } else if (step.game) {
                          setExpandedStep((cur) =>
                            cur === step.id ? null : step.id
                          );
                        }
                      }
                    }}
                    sx={{
                      cursor: clickable ? "pointer" : "default",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          lineHeight: 1.3,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {step.label}
                      </Typography>
                      {step.detail && (
                        <Typography variant="body2" color="text.secondary">
                          {step.detail}
                        </Typography>
                      )}
                      {!step.confirmed && (
                        <Typography variant="caption" color="warning.main">
                          Avancement non confirmé
                        </Typography>
                      )}
                    </Box>
                    {step.roundId && step.confirmed && (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenRound(step.roundId!);
                        }}
                        sx={{
                          ...ctaTextSx(theme),
                          flexShrink: 0,
                          minWidth: 0,
                        }}
                      >
                        Tour
                      </Button>
                    )}
                  </Box>
                  {step.game && (
                    <Collapse in={open}>
                      <JourneyMatchDetail
                        game={step.game}
                        licence={licence}
                        players={players}
                      />
                    </Collapse>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {journey.poolMatches.length > 0 && (
        <Box sx={{ ...surfaceSx(theme), p: { xs: 1.75, sm: 2 } }}>
          <Typography
            variant="overline"
            sx={{
              display: "block",
              fontWeight: 800,
              letterSpacing: "0.1em",
              color: accent,
              mb: 1,
            }}
          >
            Rencontres de poule
          </Typography>
          {journey.poolMatches.map((game) => (
            <PoolMatchRow key={game.id} game={game} players={players} />
          ))}
        </Box>
      )}
    </Stack>
  );
}
